import { motion } from "framer-motion";
import { SubpageHeader } from "../components/SubpageHeader";
import { Reveal } from "../components/Reveal";
import { useLanguage } from "../i18n/LanguageContext";
import { useSEO, breadcrumbLd } from "../components/SEO";

export default function MissaoPage() {
    const { t } = useLanguage();
    useSEO({
        title: t.seo.missao.title,
        description: t.seo.missao.desc,
        jsonLd: breadcrumbLd([{ name: t.bc.home, path: "/" }, { name: t.bc.missao, path: "/missao" }]),
    });
    return (
        <div className="min-h-screen bg-[#070503] text-[#efe6d6] relative overflow-hidden" data-testid="missao-page">
            {/* Fundo fixo: a imagem fica parada e a informação rola por cima */}
            <div className="fixed inset-0 z-0 pointer-events-none" aria-hidden="true">
                <img loading="eager" decoding="async" src="/images/missao-fundo.webp" alt="" className="w-full h-full object-cover object-center" data-testid="missao-fundo" />
                <div className="absolute inset-0 bg-[#070503]/55" />
            </div>
            <SubpageHeader />
            <motion.main
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-10 mx-auto max-w-[1100px] px-5 sm:px-10 pt-44 sm:pt-36 pb-24"
            >
                <Reveal className="text-center">
                    <p className="font-caps text-[11px] tracking-[0.35em] uppercase text-[#c9a24b]">{t.missao.label}</p>
                    <div className="flex justify-center mt-4">
                        <img loading="lazy" decoding="async" src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-16 opacity-90" />
                    </div>
                    <h1 className="font-display font-medium text-3xl sm:text-5xl lg:text-6xl leading-[1.12] text-[#efe6d6] mt-6 max-w-4xl mx-auto" data-testid="missao-title">
                        {t.missao.tituloA}<span className="gold-text">{t.missao.tituloGold}</span>{t.missao.tituloB}
                    </h1>
                </Reveal>
                <Reveal delay={0.15} className="mt-10 max-w-3xl mx-auto text-center">
                    <p className="font-display italic font-light text-lg sm:text-xl gold-text leading-relaxed" data-testid="missao-subtitulo">
                        {t.missao.subtitulo}
                    </p>
                </Reveal>

                {/* Manifesto dividido em capítulos, com painel de leitura (igual às páginas de artista) */}
                <div className="mt-16 sm:mt-20 max-w-3xl mx-auto border border-[#c9a24b]/25 bg-[#070503]/65 backdrop-blur-sm px-6 py-10 sm:px-12 sm:py-12" data-testid="missao-capitulos">
                    {t.missao.capitulos.map((c, i) => (
                        <Reveal key={c.titulo} className={i > 0 ? "mt-14 sm:mt-16" : ""}>
                            {i > 0 && <div className="h-px w-16 bg-[#c9a24b]/25 mb-10 sm:mb-12" aria-hidden="true" />}
                            <p className="font-caps text-[11px] tracking-[0.35em] uppercase text-[#c9a24b]" data-testid={`missao-capitulo-titulo-${i}`}>
                                {c.titulo}
                            </p>
                            <div className="mt-5 space-y-5">
                                {c.paragrafos.map((p, j) => (
                                    <p
                                        key={j}
                                        className={`font-display font-light text-base sm:text-lg text-[#efe6d6]/75 leading-relaxed${i === 0 && j === 0
                                            ? " first-letter:font-display first-letter:font-medium first-letter:gold-text first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:leading-[0.85]"
                                            : ""}`}
                                        data-testid={`missao-capitulo-${i}-p${j}`}
                                    >
                                        {p}
                                    </p>
                                ))}
                            </div>
                        </Reveal>
                    ))}
                </div>
                <div className="mt-16 grid sm:grid-cols-3 gap-6">
                    {t.missao.valores.map((v, i) => (
                        <Reveal key={v.n} delay={i * 0.1}>
                            <div
                                className="border border-[#c9a24b]/20 bg-[#0a0705]/70 backdrop-blur-sm p-8 sm:p-10 text-center h-full hover:border-[#c9a24b]/50 transition-colors duration-500"
                                data-testid={`missao-value-${i}`}
                            >
                                <span className="font-display gold-text text-3xl">{v.n}</span>
                                <h3 className="font-caps text-sm tracking-[0.2em] uppercase text-[#efe6d6] mt-4">{v.title}</h3>
                                <p className="text-sm font-light text-[#efe6d6]/60 leading-relaxed mt-3">{v.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
                <Reveal className="mt-20 max-w-2xl mx-auto text-center space-y-7" data-testid="missao-fecho">
                    <p className="font-display font-light text-lg sm:text-xl text-[#efe6d6]/75 leading-relaxed" data-testid="missao-fecho-a">
                        {t.missao.fechoA}
                    </p>
                    <p className="font-display italic font-light text-xl sm:text-2xl text-[#efe6d6]/80 leading-relaxed" data-testid="missao-fecho-b">
                        {t.missao.fechoB}
                    </p>
                    <div className="flex justify-center pt-2">
                        <img loading="lazy" decoding="async" src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-20 opacity-90" />
                    </div>
                    <p className="font-display italic font-medium gold-text text-3xl sm:text-4xl leading-snug pt-2" data-testid="missao-fecho-final">
                        {t.missao.fechoFinal}
                    </p>
                </Reveal>
            </motion.main>
        </div>
    );
}
